import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import { NgClass } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  Signal,
  TemplateRef,
  ViewChild,
  ViewContainerRef,
  ViewEncapsulation
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatProgressBar } from '@angular/material/progress-bar';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatTooltip } from '@angular/material/tooltip';
import { ExcelService } from '@app/core/services/excel.service';

@Component({
  selector: 'download-manager',
  imports: [MatIcon, MatIconButton, MatProgressBar, MatProgressSpinner, MatTooltip, NgClass],
  templateUrl: './download-manager.component.html',
  styleUrl: './download-manager.component.scss',
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class DownloadManagerComponent {
  @ViewChild('downloadManagerOrigin') private downloadManagerOrigin: MatButton;
  @ViewChild('downloadManagerPanel') private downloadManagerPanel: TemplateRef<any>;

  protected readonly destroyRef: DestroyRef = inject(DestroyRef);
  protected readonly excelService: ExcelService = inject(ExcelService);
  protected readonly overlay: Overlay = inject(Overlay);
  protected readonly viewContainerRef: ViewContainerRef = inject(ViewContainerRef);

  // expose files as an array of { key, name, isDone, url } derived from jobDownloadsMapping
  protected readonly files: Signal<{ key: string; name: string; isDone: boolean; url?: string | null }[]> = computed(() =>
    Object.entries(this.excelService.jobDownloadsMapping())
      .filter(([_, val]: [string, { name: string; isDone?: boolean; isFailed?: boolean; url?: string | null }]) => !val.isFailed)
      .map(([key, val]: [string, { name: string; isDone?: boolean; isFailed?: boolean; url?: string | null }]) => ({
        key,
        name: val.name,
        isDone: val.isDone,
        url: val.url
      }))
  );

  // true when any download is still in progress
  protected readonly anyLoading: Signal<boolean> = computed(() => this.files().some((f) => !f.isDone));

  // helper to trigger download when user clicks the link (service will fetch and save)
  downloadFile(item: { key: string; name: string; isDone: boolean; url?: string | null }): void {
    if (!item?.url) {
      return;
    }
    this.excelService.downloadExcel(item.name, item.url);
  }

  private _overlayRef: OverlayRef;

  openPanel(): void {
    if (!this.downloadManagerPanel) {
      return;
    }

    if (!this._overlayRef) {
      this._createOverlay();
    }

    this._overlayRef.attach(new TemplatePortal(this.downloadManagerPanel, this.viewContainerRef));
  }

  protected closePanel(): void {
    if (this._overlayRef) {
      this._overlayRef.detach();
    }
  }

  private _createOverlay(): void {
    this._overlayRef = this.overlay.create({
      hasBackdrop: true,
      backdropClass: 'seal-backdrop-on-mobile',
      scrollStrategy: this.overlay.scrollStrategies.block(),
      positionStrategy: this.overlay
        .position()
        .flexibleConnectedTo(this.downloadManagerOrigin._elementRef.nativeElement)
        .withLockedPosition(true)
        .withPush(true)
        .withPositions([
          {
            originX: 'start',
            originY: 'bottom',
            overlayX: 'start',
            overlayY: 'top'
          },
          {
            originX: 'start',
            originY: 'top',
            overlayX: 'start',
            overlayY: 'bottom'
          },
          {
            originX: 'end',
            originY: 'bottom',
            overlayX: 'end',
            overlayY: 'top'
          },
          {
            originX: 'end',
            originY: 'top',
            overlayX: 'end',
            overlayY: 'bottom'
          }
        ])
    });

    this._overlayRef
      .backdropClick()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this._overlayRef.detach();
      });
  }
}
